import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { CLOUD, CLOUD_TOP, EYE, FORELOCK, SHOULDER_TUFT, SHOULDER_TUFT_EDGE, SheepHover, SheepPose, WOOL } from './sheep-hover';

/** Which element and attribute each part of a pose drives. */
const PARTS: [keyof SheepPose, string, string][] = [
  ['cloud', '.tsh-cloud', 'transform'],
  ['shadow', '.tsh-shadow', 'transform'],
  ['shadowOpacity', '.tsh-shadow', 'opacity'],
  ['sheep', '.tsh-sheep', 'transform'],
  ['farWing', '.tsh-wing--far path', 'd'],
  ['farWingLines', '.tsh-wing--far .tsh-wing__lines', 'd'],
  ['farLegs', '.tsh-legs--far', 'd'],
  ['nearLegs', '.tsh-legs--near', 'd'],
  ['wool', '.tsh-wool', 'transform'],
  ['head', '.tsh-head', 'transform'],
  ['eye', '.tsh-eye', 'transform'],
  ['nearWing', '.tsh-wing--near path', 'd'],
  ['nearWingLines', '.tsh-wing--near .tsh-wing__lines', 'd']
];

/**
 * The winged sheep above the About page title, hovering over its cloud (see SheepHover).
 * It pauses while off-screen and holds still for visitors who prefer reduced motion.
 */
@Component({
  selector: 'app-winged-sheep',
  templateUrl: './winged-sheep.component.html',
  styleUrls: ['./winged-sheep.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WingedSheepComponent implements AfterViewInit, OnDestroy {

  readonly cloud = CLOUD;
  readonly cloudTop = CLOUD_TOP;
  readonly wool = WOOL;
  readonly forelock = FORELOCK;
  readonly shoulderTuft = SHOULDER_TUFT;
  readonly shoulderTuftEdge = SHOULDER_TUFT_EDGE;
  readonly eye = EYE;

  /** The still pose in the markup; once hovering, the parts are set directly each frame. */
  readonly pose = SheepHover.restingPose();

  private observer?: IntersectionObserver;
  private frame = 0;
  private stopped = false;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {
  }

  ngAfterViewInit() {
    if (typeof IntersectionObserver === 'undefined'
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const root = this.host.nativeElement;
    const parts = PARTS.map(([key, selector, attribute]) => ({ key, element: root.querySelector(selector), attribute }));
    const hover = new SheepHover();
    let last: number | undefined;

    const tick = (now: number) => {
      // Cap the step so a dropped frame or a background tab doesn't make it jump.
      const dt = last === undefined ? 0 : Math.min(.05, (now - last) / 1000);
      last = now;
      const pose = hover.advance(dt);
      for (const { key, element, attribute } of parts) {
        element?.setAttribute(attribute, pose[key]);
      }
      if (!this.stopped) {
        this.frame = requestAnimationFrame(tick);
      }
    };

    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(([entry]) => {
        // Time stands still while it is off-screen.
        cancelAnimationFrame(this.frame);
        if (entry.isIntersecting && !this.stopped) {
          last = undefined;
          this.frame = requestAnimationFrame(tick);
        }
      });
      this.observer.observe(root);
    });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.stopped = true;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
  }
}
